import React, { useEffect, useMemo, useState } from "react";
import "../styles/Gallery.css";
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

const API_BASE = process.env.REACT_APP_API_BASE || "";

function IconChevronLeft({ size = 28 }) { return <svg width={size} height={size} viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>; }
function IconChevronRight({ size = 28 }) { return <svg width={size} height={size} viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>; }
function IconClose({ size = 26 }) { return <svg width={size} height={size} viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLineLength="round" /></svg>; }

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const { t } = useTranslation();

  // 데이터 로드 및 이미지 비율 판별
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/gallery_list.php`, { credentials: "include" });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed");

        const metaPromises = data.map((it) => {
          return new Promise((resolve) => {
            const img = new Image();
            img.src = `${API_BASE}${it.thumb_path || it.image_path}`;
            img.onload = () => {
              const ratio = img.width / img.height;
              let type = "p-normal"; // 기본형

              if (ratio > 1.4) type = "l-wide";      // 넓은 가로 (기존 2, 1)
              else if (ratio > 1.1) type = "l-slim"; // 중간 가로 (주연님의 1.5, 1)
              else if (ratio < 0.6) type = "p-tall"; // 아주 긴 세로 (기존 1, 2)
              else if (ratio < 0.8) type = "p-mid";  // 중간 세로 (주연님의 1, 1.5)
              
              resolve({ ...it, viewType: type });
            };
            img.onerror = () => resolve({ ...it, viewType: "p-normal" });
          });
        });

        const results = await Promise.all(metaPromises);
        setItems(results);
      } catch (e) {
        setErr(String(e.message || e));
      }
    })();
  }, []);

  const sorted = useMemo(() => [...items].sort((a, b) => (b.id ?? 0) - (a.id ?? 0)), [items]);
  const total = sorted.length;
  const current = sorted[idx];

  const close = () => setOpen(false);
  const openAt = (i) => { setIdx(i); setOpen(true); };
  const prev = () => setIdx((p) => (p - 1 + total) % total);
  const next = () => setIdx((p) => (p + 1) % total);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, total]);

  return (
    <>
      <Helmet>
        <title>배우 한지우 갤러리</title>
      </Helmet>
      <div className="sub-page-container gallery-page">
        <h1 className="section-title">{t('nav_gallery')}</h1>
        {err && <p className="err-msg">{err}</p>}

        <div className="gallery-grid-container">
          {sorted.map((it, i) => (
            <button
              key={it.id}
              type="button"
              className={`gitem ${it.viewType || "p-normal"}`}
              onClick={() => openAt(i)}
            >
              <img 
                src={`${API_BASE}${it.thumb_path || it.image_path}`} 
                alt={it.title || "배우 한지우 이미지"} 
                loading="lazy"
              />
            </button>
          ))}
        </div>

        {/* Lightbox */}
        {open && current && (
          <div className="lightbox" role="dialog" onClick={(e) => {
            if (e.target.classList.contains('lightbox') || e.target.classList.contains('lb-stage')) close();
          }}>
            <button className="lb-close" onClick={close}><IconClose /></button>
            <button className="lb-nav lb-prev" onClick={prev}><IconChevronLeft /></button>
            <div className="lb-stage">
              <img className="lb-img" src={`${API_BASE}${current.image_path}`} alt={current.title} />
            </div>
            <button className="lb-nav lb-next" onClick={next}><IconChevronRight /></button>
          </div>
        )}
      </div>
    </>
  );
}